
import { useMemo, useCallback } from "react";
import { Invoice } from "@/types/payments";
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { useFormatters } from "@financialharmony/ui";
import { useLatest } from '@/hooks/useLatest';
import {
  getClosestBusinessDayBefore,
  isBankingHoliday,
  formatDateToYYYYMMDD
} from "@/utils/dateUtils";

/**
 * Hook for managing payment date adjustments and validations
 * Uses useMemo for automatic adjustments (derived state) to avoid infinite loops
 * Returns factory function for manual updates to maintain proper React data flow
 */
export function usePaymentDateManager(invoices: Invoice[]) {
  const t = useTranslations('consultantToasts');
  const formatDateRef = useLatest(useFormatters().formatDate);

  // Automatically adjust payment dates for past dates and banking holidays
  // This is derived state - computed from props, not stored
  const adjustedInvoices = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison

    return invoices.map(invoice => {
      const dueDate = new Date(invoice.dueDate);
      let paymentDate = invoice.paymentDate ? new Date(invoice.paymentDate) : dueDate;
      let needsAdjustment = false;

      // #498 whole-branch review finding I3a: the "a payment date can't be in the past" adjustment
      // below must only apply to an item that is STILL genuinely unpaid/unresolved — never to an
      // already-resolved ('paid'/'manually_handled') payable being reopened for correction.
      // PayableMatchSuggestionsModal prefills its date field directly from `payable.paymentDate`
      // (design spec Flow 7 step 2, "shows the previously entered payment details") — every
      // payable reaching that modal has already passed through THIS hook first. Before this gate,
      // reopening an already-resolved payable whose real acknowledgment date was in the past
      // silently showed today's date instead, and confirming (even to fix something unrelated,
      // e.g. a note) would silently overwrite the original payment date. Terminal ('paid',
      // 'manually_handled') statuses are the two this correction/reopen flow can reach (see
      // PayableMatchSuggestionsModal's buildCorrectionBanner, gated on the same pair) — every
      // other status is still genuinely open/in-flight, where forcing a stale computed date
      // forward to today is the original, intended behavior.
      const isResolved = invoice.status === 'paid' || invoice.status === 'manually_handled';

      // Check for past payment dates
      if (!isResolved && paymentDate < today) {
        paymentDate = new Date(today);
        needsAdjustment = true;
      }

      // Check for banking holidays for unpaid invoices without a specifically set payment date
      if ((invoice.status === 'unpaid' || invoice.status === 'overdue') &&
          (!invoice.paymentDate || invoice.paymentDate === invoice.dueDate)) {
        if (isBankingHoliday(dueDate)) {
          paymentDate = getClosestBusinessDayBefore(dueDate);
          needsAdjustment = true;
        }
      }

      // Only create new object if adjustment is needed (optimization)
      if (needsAdjustment) {
        return {
          ...invoice,
          paymentDate: formatDateToYYYYMMDD(paymentDate)
        };
      }

      return invoice;
    });
  }, [invoices]);

  // Factory function for creating the update handler
  // This allows parent to control state mutations while keeping logic here
  const createUpdatePaymentDate = useCallback((
    setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>
  ) => {
    return (invoiceId: string, date: Date) => {
      // Create a new date object to avoid mutation issues
      const selectedDate = new Date(date);

      // Reset time component to midnight UTC to prevent timezone issues
      selectedDate.setUTCHours(0, 0, 0, 0);

      // Get today's date and normalize for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Ensure date is not before today
      if (selectedDate < today) {
        toast.error(t('paymentDate.notPossibleTitle'), {
          description: t('paymentDate.notPossibleBody'),
        });
        return; // Don't update with an invalid date
      }

      let finalDate = selectedDate;

      // Check if the selected date is a banking holiday
      if (isBankingHoliday(selectedDate)) {
        finalDate = getClosestBusinessDayBefore(selectedDate);

        toast.success(t('paymentDate.adjustedTitle'), {
          description: t('paymentDate.adjustedBody', { date: formatDateRef.current(finalDate) }),
        });
      }

      // Format the final date as YYYY-MM-DD
      const formattedDate = formatDateToYYYYMMDD(finalDate);

      // Update state through parent's setter
      setInvoices(prevInvoices =>
        prevInvoices.map(invoice =>
          invoice.id === invoiceId
            ? { ...invoice, paymentDate: formattedDate }
            : invoice
        )
      );

      // Check if payment date is after due date (allow it, but show warning)
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (invoice && finalDate > new Date(invoice.dueDate)) {
        toast.error(t('paymentDate.lateTitle'), {
          description: t('paymentDate.lateBody', { date: formatDateRef.current(invoice.dueDate) }),
        });
      }
    };
  }, [invoices, t, formatDateRef]);

  return { adjustedInvoices, createUpdatePaymentDate };
}
